import { PicturePreviewContainer } from '@components/Card/CardHeader/CardHeaderPictures/PicturePreview';
import { cardEditContext } from '@context/card-edit-context';
import type { CardDetailsFragment } from '@graphql/fragments/cards/core.graphql';
import { useTeamRole } from '@hooks/team-workspace/use-team-role';
import { useBlockAttributes } from 'app-next/[workspace]/cards/hooks/use-block-attribibute';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { useContext } from 'react';

import { PicturePreviewButton } from '../CardHeaderPictures';
import type { ImageType, ImageUrls } from '../types';
import { cardImageAttributeNameToImageType } from '../utilities';
import type { CardAttributeType } from '.cache/__types__';

interface SelectImageTypeContentProps {
  imgUrls?: ImageUrls | null;
  canLock?: boolean;
  card?: Maybe<CardDetailsFragment>;
  disabled?: boolean;
  onSelect: (type: ImageType) => void;
}

export const SelectImageTypeContent: React.FC<SelectImageTypeContentProps> = ({
  canLock,
  card,
  onSelect,
  imgUrls,
  disabled,
}) => {
  const { isCardOwner } = useContext(cardEditContext);
  const { atLeastRole } = useTeamRole();

  // Members assigned to this card shouldn't be able to lock/unlock images.
  // Admins and owners can always lock/unlock, even on cards assigned to themselves.
  const isAssignedMember = isCardOwner && !atLeastRole('ADMIN');
  const canLockImages = isAssignedMember ? false : canLock;

  const { toggleBlockAttribute, categorisedAttributes, isAttributeBlocked } = useBlockAttributes({
    categoryAllowList: ['image'],
    card,
  });

  const templateFilter = (attribute: CardAttributeType) => {
    if (attribute?.name === 'embedded_qr_code_image') {
      return false;
    }
    return true;
  };

  const attributesBlockedByTemplate = card?.template?.blocked_attributes?.map((item) => item.id);

  return (
    <PicturePreviewContainer>
      {categorisedAttributes.image?.filter(templateFilter).map((attribute) => {
        const type = cardImageAttributeNameToImageType(attribute?.name);
        const blockedByTemplate = attributesBlockedByTemplate?.includes(attribute.id);
        return (
          <PicturePreviewButton
            image={imgUrls?.[type]}
            canLock={canLockImages}
            required={false}
            key={type}
            type={type}
            isBlocked={isAttributeBlocked(attribute?.id)}
            blockedByTemplate={blockedByTemplate}
            disabled={disabled ?? false}
            toggleBlockAttribute={() => toggleBlockAttribute(attribute?.id)}
            onClick={() => {
              onSelect(type);
            }}
          />
        );
      })}
    </PicturePreviewContainer>
  );
};
